import React, { useEffect } from 'react';
import { Platform } from 'react-native';
import { Box } from '@gluestack-ui/themed';

import { DASHBOARD_EASE_OUT_CSS, DASHBOARD_MOTION } from './dashboardMotion';

const SECTION_FADE_STYLE_ID = 'dashboard-section-fade-keyframes';

function ensureSectionFadeKeyframes() {
  if (Platform.OS !== 'web' || typeof document === 'undefined') {
    return;
  }
  if (document.getElementById(SECTION_FADE_STYLE_ID)) {
    return;
  }
  const style = document.createElement('style');
  style.id = SECTION_FADE_STYLE_ID;
  style.textContent = `
    @keyframes dashboardSectionFadeIn {
      from { opacity: 0; }
    }
  `;
  document.head.appendChild(style);
}

export type DashboardSectionEnterProps = {
  reduceMotion: boolean;
  /** 0-based section index for stagger. */
  sectionIndex: number;
  children: React.ReactNode;
  testID?: string;
};

/** Web animation + transition values for tests and Visual Lab. */
export type SectionEnterMotionStyle = {
  animationName: string;
  animationDuration: string;
  animationTimingFunction: string;
  animationDelay: string;
  animationFillMode: 'both';
  transitionDuration: string;
};

export function sectionEnterMotionStyle(
  reduceMotion: boolean,
  sectionIndex: number,
): SectionEnterMotionStyle {
  const animationDuration = reduceMotion
    ? `${DASHBOARD_MOTION.sectionEnterReducedMs}ms`
    : `${DASHBOARD_MOTION.sectionEnterMs}ms`;
  const animationDelay = reduceMotion
    ? '0ms'
    : `${sectionIndex * DASHBOARD_MOTION.sectionStaggerMs}ms`;
  return {
    animationName: 'dashboardSectionFadeIn',
    animationDuration,
    animationTimingFunction: reduceMotion ? 'linear' : DASHBOARD_EASE_OUT_CSS,
    animationDelay,
    animationFillMode: 'both',
    transitionDuration: '0ms',
  };
}

/**
 * Opacity-only section enter via CSS animation (not transition).
 */
export function DashboardSectionEnter({
  reduceMotion,
  sectionIndex,
  children,
  testID = 'dashboard-section-enter',
}: DashboardSectionEnterProps) {
  useEffect(() => {
    ensureSectionFadeKeyframes();
  }, []);

  const motion = sectionEnterMotionStyle(reduceMotion, sectionIndex);

  return (
    <Box
      testID={testID}
      sx={{
        _web: {
          animationName: motion.animationName,
          animationDuration: motion.animationDuration,
          animationTimingFunction: motion.animationTimingFunction,
          animationDelay: motion.animationDelay,
          animationFillMode: motion.animationFillMode,
          transitionDuration: motion.transitionDuration,
        },
      }}
    >
      {children}
    </Box>
  );
}
